// JV simple-voucher import configuration.
// Extracted from journal.tsx so page-agnostic config lives separately from
// page state.  parse() and runImport() stay in journal.tsx because they close
// over page-level mutations/masters.

import { fmt } from '@dcc-erp/shared';
import type { ChartOfAccount, CreateJournalEntry } from '@dcc-erp/shared';

import type { ImportFieldSpec, PreviewColumn } from '../../components/SpreadsheetImportModal';

// ---- Simple-voucher import (one row = one balanced JV) ----------------------

// Template / header keys for the simple-voucher importer. ONE spreadsheet row =
// ONE balanced voucher with exactly one debit account + one credit account of
// equal amount. Multi-line vouchers stay in the "สร้างใบสำคัญ" modal.
export const JV_IMPORT_COLS = [
  'voucherDate',
  'period',
  'prefix',
  'description',
  'debitAccountCode',
  'creditAccountCode',
  'amount',
  'branchCode',
  'departmentCode',
] as const;

export const JV_IMPORT_FIELDS: ImportFieldSpec[] = [
  {
    col: 'voucherDate',
    label: 'วันที่ใบสำคัญ',
    required: true,
    hint: 'รูปแบบ YYYY-MM-DD เช่น 2026-05-12',
  },
  {
    col: 'period',
    label: 'งวด (YYYYMM)',
    required: false,
    hint: 'เว้นว่างได้ — จะคำนวณจากวันที่ เช่น 202605',
  },
  {
    col: 'prefix',
    label: 'ประเภทเอกสาร',
    required: false,
    hint: 'เว้นว่างได้ — ค่าเริ่มต้น JV',
  },
  {
    col: 'description',
    label: 'รายละเอียด',
    required: true,
    hint: 'เช่น บันทึกค่าใช้จ่ายค่าน้ำมัน',
  },
  {
    col: 'debitAccountCode',
    label: 'รหัสบัญชีเดบิต',
    required: true,
    hint: 'รหัสบัญชีฝั่งเดบิต เช่น 5310',
  },
  {
    col: 'creditAccountCode',
    label: 'รหัสบัญชีเครดิต',
    required: true,
    hint: 'รหัสบัญชีฝั่งเครดิต เช่น 1111',
  },
  {
    col: 'amount',
    label: 'จำนวนเงิน',
    required: true,
    hint: 'จำนวนเงิน > 0 (เดบิต = เครดิต = ค่านี้)',
  },
  {
    col: 'branchCode',
    label: 'รหัสสาขา',
    required: false,
    hint: 'เว้นว่างได้ — ถ้าระบุ ต้องมีอยู่จริง',
  },
  {
    col: 'departmentCode',
    label: 'รหัสหน่วยงาน',
    required: false,
    hint: 'เว้นว่างได้ — ถ้าระบุ ต้องมีอยู่จริง',
  },
];

export const JV_TEMPLATE_EXAMPLES: (string | number)[][] = [
  ['2026-05-12', '', 'JV', 'บันทึกค่าน้ำมันรถ', '5310', '1111', 1500, '', ''],
  ['2026-05-12', '202605', 'JV', 'รับชำระลูกหนี้', '1111', '1140', 20000, '', ''],
];

// Preview columns for the simple-voucher importer (declared once at module
// scope so it isn't re-created on every render).
export const JV_PREVIEW_COLS: PreviewColumn<CreateJournalEntry>[] = [
  { header: 'วันที่', width: 100, render: (d) => d.voucherDate },
  { header: 'รายละเอียด', render: (d) => d.description },
  {
    header: 'เดบิต → เครดิต',
    width: 160,
    render: (d) => `${d.lines[0]?.accountCode ?? ''} → ${d.lines[1]?.accountCode ?? ''}`,
  },
  {
    header: 'จำนวนเงิน',
    width: 110,
    render: (d) => fmt(Number(d.lines[0]?.debit ?? 0)),
  },
];

// 'YYYY-MM-DD' → 'YYYYMM' for the period code (per Phase 3b convention).
export function periodFromDate(iso: string): string {
  if (iso.length < 7) return '';
  return iso.slice(0, 4) + iso.slice(5, 7);
}

// ---- Paste-from-Excel parser ------------------------------------------------

export interface PastedLine {
  accountCode: string;
  description: string;
  debit: string;
  credit: string;
}

function cleanNum(s: string | undefined): string {
  if (!s) return '0';
  const v = s.replace(/[^\d.]/g, '');
  return v || '0';
}

// Parse clipboard TSV (Excel copy) into draft lines.
// Columns, in order: รหัสบัญชี | คำอธิบาย | เดบิต | เครดิต.
// The account cell may be a bare code, a "code — name" label, or a full name.
export function parsePastedLines(text: string, coa: ChartOfAccount[]): PastedLine[] {
  const codes = new Set(coa.map((c) => c.code));
  const rows = text
    .split(/\r?\n/)
    .map((r) => r.trim())
    .filter(Boolean);
  const out: PastedLine[] = [];
  for (const row of rows) {
    const cols = row.split('\t');
    const first = (cols[0] ?? '').trim();
    if (/^(รหัส|account)/i.test(first)) continue; // header row
    let accountCode = (first.split(/\s|—/)[0] ?? '').trim();
    if (accountCode && !codes.has(accountCode)) {
      const byName = coa.find((c) => `${c.code} — ${c.nameTh}` === first || c.nameTh === first);
      if (byName) accountCode = byName.code;
    }
    const description = (cols[1] ?? '').trim();
    const debit = cleanNum(cols[2]);
    const credit = cleanNum(cols[3]);
    if (!accountCode && !description && debit === '0' && credit === '0') continue;
    out.push({ accountCode, description, debit, credit });
  }
  return out;
}
